/**
 * Pure PCM16 <-> base64 conversion helpers for the Voice Live audio paths.
 */

/**
 * Decode a base64-encoded PCM16 (little-endian) audio chunk into a normalized
 * Float32Array in the range [-1, 1). Returns null when the input is not valid
 * base64 or is too short to contain a single 16-bit sample.
 */
export const decodeBase64Pcm16ToFloat32 = (base64Audio: string): Float32Array | null => {
  // base64 -> bytes -> Int16 PCM (little-endian) -> Float32 [-1, 1]
  let binaryString: string;
  try {
    binaryString = atob(base64Audio);
  } catch {
    return null;
  }
  const byteLength = binaryString.length;
  if (byteLength < 2) return null;
  const bytes = new Uint8Array(byteLength);
  for (let i = 0; i < byteLength; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const sampleCount = byteLength >> 1; // 2 bytes per PCM16 sample
  const int16 = new Int16Array(bytes.buffer, 0, sampleCount);
  const float32 = new Float32Array(sampleCount);
  for (let i = 0; i < sampleCount; i++) {
    float32[i] = int16[i] / 32768;
  }
  return float32;
};

/**
 * Encode a PCM16 buffer (as produced by the audio-capture worklet) into a
 * base64 string for transport over the WebSocket. Processed in chunks to avoid
 * the call-stack and memory pressure of spreading large arrays.
 */
export const encodePcm16ToBase64 = (pcm16Buffer: ArrayBuffer): string => {
  const uint8Array = new Uint8Array(pcm16Buffer);
  let binaryString = '';
  const chunkSize = 8192; // Process in chunks to reduce memory pressure
  for (let i = 0; i < uint8Array.length; i += chunkSize) {
    const chunk = uint8Array.subarray(i, Math.min(i + chunkSize, uint8Array.length));
    binaryString += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binaryString);
};
